import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Tracks app-builder creations that have been kicked off but whose real install
// (with its AI-generated name + icon) hasn't yet surfaced in the sidebar's
// getInstalls cache. Each entry renders a temporary placeholder sidebar item
// until the real install for the same appBuilderId arrives.
export interface AppBuilderPlaceholder {
  // Client-generated id, assigned the moment the build is kicked off (before we
  // have a real appBuilderId from the backend).
  id: string;
  // Set once prepare-build resolves. Used to reconcile the placeholder against
  // the real install once it appears in getInstalls.
  appBuilderId?: string;
}

export interface AppBuilderPlaceholderState {
  placeholders: AppBuilderPlaceholder[];
}

const initialState: AppBuilderPlaceholderState = { placeholders: [] };

const appBuilderPlaceholderSlice = createSlice({
  name: 'appBuilderPlaceholder',
  initialState,
  reducers: {
    addAppBuilderPlaceholder(state, action: PayloadAction<{ id: string }>) {
      if (state.placeholders.some((p) => p.id === action.payload.id)) return;
      state.placeholders.push({ id: action.payload.id });
    },
    setPlaceholderAppBuilderId(
      state,
      action: PayloadAction<{ id: string; appBuilderId: string }>,
    ) {
      const placeholder = state.placeholders.find(
        (p) => p.id === action.payload.id,
      );
      if (placeholder) placeholder.appBuilderId = action.payload.appBuilderId;
    },
    removeAppBuilderPlaceholder(state, action: PayloadAction<{ id: string }>) {
      state.placeholders = state.placeholders.filter(
        (p) => p.id !== action.payload.id,
      );
    },
  },
});

export const {
  addAppBuilderPlaceholder,
  setPlaceholderAppBuilderId,
  removeAppBuilderPlaceholder,
} = appBuilderPlaceholderSlice.actions;

export const appBuilderPlaceholderReducer = appBuilderPlaceholderSlice.reducer;
